import * as THREE from 'three'
import { useShipStore } from '@/stores/shipStore.ts'
import { useFleetStore } from '@/stores/fleetStore.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import { useMapSettingsStore } from '@/stores/mapSettingsStore.ts'
import type PlayerShip from '@/types/PlayerShip.ts'
import { zoomFactor } from '@/enums/zoomFactor.ts'

export class MapAllShipsActionPaths {
    scene: THREE.Scene
    shipStore: any
    fleetStore: any
    mapStore: any
    mapSettingsStore: any

    // Line segments objects for different types
    shipActionLines: THREE.LineSegments | null = null
    fleetActionLines: THREE.LineSegments | null = null

    // Materials for different types
    shipMaterial: THREE.Material | null = null
    fleetMaterial: THREE.Material | null = null

    // Animation removed - using solid lines instead of animated ants

    // Performance tracking
    lastUpdateHash: string = ''
    updateInterval: number | null = null

    constructor(scene: THREE.Scene) {
        this.scene = scene
        this.shipStore = useShipStore()
        this.fleetStore = useFleetStore()
        this.mapStore = useMapStore()
        this.mapSettingsStore = useMapSettingsStore()

        this.createMaterials()

        // Initial update if setting is already enabled
        if (this.mapSettingsStore.showAllShipActionPaths) {
            setTimeout(() => {
                this.updateActionPaths()
            }, 100)
        }
    }

    createMaterials() {
        // Material for individual ship paths - dark gray
        this.shipMaterial = new THREE.LineBasicMaterial({
            color: 0x404040, // Dark gray
            transparent: true,
            opacity: 0.7
        })

        // Material for fleet paths - darker blue
        this.fleetMaterial = new THREE.LineBasicMaterial({
            color: 0x2E5A96, // Darker blue
            transparent: true,
            opacity: 0.8
        })
    }

    // Generate a hash of current ship and fleet actions for change detection
    generateActionsHash(): string {
        const ships = Array.from(this.shipStore.ships.values()) as PlayerShip[]
        const fleets = Array.from(this.fleetStore.fleets.values())

        const shipData = ships
            .filter(ship => ship.fleetId === 0) // Only include ships not in fleets
            .map(ship => {
                if (!ship.actions || ship.actions.length === 0) return `s${ship.id}:${ship.xPos},${ship.yPos}:empty`
                return `s${ship.id}:${ship.xPos},${ship.yPos}:${ship.actions.map(a => `${a.action}:${a.targetX},${a.targetY}`).join('|')}`
            }).join(';')

        const fleetData = fleets
            .map(fleet => {
                if (!fleet.actions || fleet.actions.length === 0) return `f${fleet.id}:${fleet.xPos},${fleet.yPos}:empty`
                return `f${fleet.id}:${fleet.xPos},${fleet.yPos}:${fleet.actions.map(a => `${a.action}:${a.targetX},${a.targetY}`).join('|')}`
            }).join(';')

        return btoa(shipData + '|' + fleetData).substring(0, 32) // Create a hash-like string
    }

    updateActionPaths() {
        // Performance check: only update if ship/fleet actions have changed
        const currentHash = this.generateActionsHash()
        if (currentHash === this.lastUpdateHash) {
            return
        }
        this.lastUpdateHash = currentHash

        // Remove existing lines
        this.removeActionPaths()

        // Check if feature is enabled
        if (!this.mapSettingsStore.showAllShipActionPaths) {
            return
        }

        // Get current camera position for distance culling
        const camera = this.mapStore.camera
        if (!camera) {
            return
        }

        // Update both ship and fleet paths
        this.updateShipPaths()
        this.updateFleetPaths()
    }

    updateShipPaths() {
        const ships = Array.from(this.shipStore.ships.values()) as PlayerShip[]
        const nonFleetShips = ships.filter(ship => ship.fleetId === 0)

        // Group ships by location and action sequence to deduplicate paths
        const pathGroups = new Map<string, PlayerShip>()

        for (const ship of nonFleetShips) {
            if (!ship.actions || ship.actions.length === 0) continue

            // Create a key based on starting position and action sequence
            const actionKey = ship.actions
                .filter(action => action.action !== 'repair' && action.action !== 'defend' && action.action !== 'wait' &&
                               action.targetX !== 0 && action.targetY !== 0)
                .map(action => `${action.action}:${action.targetX},${action.targetY}`)
                .join('|')

            if (actionKey) {
                const groupKey = `${ship.xPos},${ship.yPos}:${actionKey}`

                // Only keep the first ship for each unique path
                if (!pathGroups.has(groupKey)) {
                    pathGroups.set(groupKey, ship)
                }
            }
        }

        const shipPoints: THREE.Vector3[] = []

        // Generate points only for unique paths
        for (const ship of pathGroups.values()) {
            let startX = ship.xPos
            let startY = ship.yPos

            for (const action of ship.actions) {
                // Skip stationary actions
                if (action.action === 'repair' || action.action === 'defend' || action.action === 'wait' ||
                    action.targetX === 0 || action.targetY === 0) {
                    continue
                }

                if (startX !== 0 && startY !== 0) {
                    // Add line segment points
                    shipPoints.push(new THREE.Vector3(startX, startY, 0.1))
                    shipPoints.push(new THREE.Vector3(action.targetX, action.targetY, 0.1))
                }

                startX = action.targetX
                startY = action.targetY
            }
        }

        // Create ship action lines if we have points
        if (shipPoints.length > 0) {
            const shipGeometry = new THREE.BufferGeometry().setFromPoints(shipPoints)
            this.shipActionLines = new THREE.LineSegments(shipGeometry, this.shipMaterial!)
            this.shipActionLines.name = 'shipActionPaths'
            this.scene.add(this.shipActionLines)
        }
    }

    updateFleetPaths() {
        const fleets = Array.from(this.fleetStore.fleets.values())

        // Group fleets by location and action sequence to deduplicate paths
        const pathGroups = new Map<string, any>()

        for (const fleet of fleets) {
            if (!fleet.actions || fleet.actions.length === 0) continue

            // Create a key based on starting position and action sequence
            const actionKey = fleet.actions
                .filter(action => action.action !== 'repair' && action.action !== 'defend' && action.action !== 'wait' &&
                               action.targetX !== 0 && action.targetY !== 0)
                .map(action => `${action.action}:${action.targetX},${action.targetY}`)
                .join('|')

            if (actionKey) {
                const groupKey = `${fleet.xPos},${fleet.yPos}:${actionKey}`

                // Only keep the first fleet for each unique path
                if (!pathGroups.has(groupKey)) {
                    pathGroups.set(groupKey, fleet)
                }
            }
        }

        const fleetPoints: THREE.Vector3[] = []

        // Generate points only for unique paths
        for (const fleet of pathGroups.values()) {
            let startX = fleet.xPos
            let startY = fleet.yPos

            for (const action of fleet.actions) {
                // Skip stationary actions
                if (action.action === 'repair' || action.action === 'defend' || action.action === 'wait' ||
                    action.targetX === 0 || action.targetY === 0) {
                    continue
                }

                if (startX !== 0 && startY !== 0) {
                    // Add line segment points (slightly higher z to differentiate)
                    fleetPoints.push(new THREE.Vector3(startX, startY, 0.12))
                    fleetPoints.push(new THREE.Vector3(action.targetX, action.targetY, 0.12))
                }

                startX = action.targetX
                startY = action.targetY
            }
        }

        // Create fleet action lines if we have points
        if (fleetPoints.length > 0) {
            const fleetGeometry = new THREE.BufferGeometry().setFromPoints(fleetPoints)
            this.fleetActionLines = new THREE.LineSegments(fleetGeometry, this.fleetMaterial!)
            this.fleetActionLines.name = 'fleetActionPaths'
            this.scene.add(this.fleetActionLines)
        }
    }

    removeActionPaths() {
        if (this.shipActionLines) {
            this.scene.remove(this.shipActionLines)
            this.shipActionLines.geometry.dispose()
            this.shipActionLines = null
        }

        if (this.fleetActionLines) {
            this.scene.remove(this.fleetActionLines)
            this.fleetActionLines.geometry.dispose()
            this.fleetActionLines = null
        }
    }

    // Animation methods removed - using solid lines instead

    setVisible(visible: boolean) {
        if (visible) {
            // Apply zoom-based visibility rules
            const currentZoom = this.mapStore.zoomFactor
            const isBeyondSector = currentZoom < zoomFactor.Sector
            const shouldShowShips = !isBeyondSector
            const shouldShowFleets = true // Always show fleets when feature is enabled

            if (this.shipActionLines) {
                this.shipActionLines.visible = shouldShowShips
            }

            if (this.fleetActionLines) {
                this.fleetActionLines.visible = shouldShowFleets
            }

            this.updateActionPaths()
            this.startPeriodicUpdates()
        } else {
            if (this.shipActionLines) {
                this.shipActionLines.visible = false
            }

            if (this.fleetActionLines) {
                this.fleetActionLines.visible = false
            }

            this.stopPeriodicUpdates()
        }
    }

    startPeriodicUpdates() {
        if (this.updateInterval) return

        // Update paths every 1 second to catch ship movements
        this.updateInterval = setInterval(() => {
            this.updateActionPaths()
        }, 1000)
    }

    stopPeriodicUpdates() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval)
            this.updateInterval = null
        }
    }

    dispose() {
        this.stopPeriodicUpdates()
        this.removeActionPaths()

        if (this.shipMaterial) {
            this.shipMaterial.dispose()
            this.shipMaterial = null
        }

        if (this.fleetMaterial) {
            this.fleetMaterial.dispose()
            this.fleetMaterial = null
        }
    }

    // Called when ships are updated via WebSocket or other means
    onShipsUpdated() {
        // Force update by clearing the hash to ensure paths are redrawn
        this.lastUpdateHash = ''
        // Immediate update for ship changes (no debounce for better responsiveness)
        this.updateActionPaths()
    }

    // Called when zoom level changes for LOD
    onZoomChanged(zoomLevel: number) {
        // Only update visibility if the feature is enabled
        if (!this.mapSettingsStore.showAllShipActionPaths) {
            return
        }

        // Hide ship paths when zoomed out beyond Sector level, but keep fleet paths
        const isBeyondSector = zoomLevel < zoomFactor.Sector // Below 0.4 (more zoomed out than Sector)
        const shouldShowShips = !isBeyondSector
        const shouldShowFleets = true // Always show fleets when feature is enabled


        // Only update visibility, don't recreate lines
        if (this.shipActionLines) {
            this.shipActionLines.visible = shouldShowShips
        }

        if (this.fleetActionLines) {
            this.fleetActionLines.visible = shouldShowFleets
        }

        // Keep updates running since fleets are always visible
        if (!this.updateInterval) {
            this.startPeriodicUpdates()
        }
    }
}